// src/components/MultiDomainCSVUploader.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as Icons from 'lucide-react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Badge,
  Avatar,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';

// Sample CSV template data for multi-domain
const SAMPLE_CSV_DATA = [
  {
    name: 'John Smith',
    email: 'john.smith@company.com',
    phone: '+1 (555) 123-4567',
    company: 'Tech Corp',
    department: 'Engineering',
    designation: 'Senior Developer',
    employeeId: 'EMP-001',
    bloodGroup: 'A+',
    location: 'New York, NY'
  },
  {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@subsidiary.com',
    phone: '+1 (555) 234-5678',
    company: 'Tech Subsidiary',
    department: 'Design',
    designation: 'UI/UX Designer',
    employeeId: 'SUB-002',
    bloodGroup: 'B+',
    location: 'San Francisco, CA'
  },
  {
    name: 'Mike Wilson',
    email: 'mike.wilson@partner.org',
    phone: '+1 (555) 345-6789',
    company: 'Partner Organization',
    department: 'Marketing',
    designation: 'Marketing Manager',
    employeeId: 'PTR-003',
    bloodGroup: 'O+',
    location: 'Chicago, IL'
  }
];

// Function to download sample CSV
const downloadSampleCSV = () => {
  const headers = Object.keys(SAMPLE_CSV_DATA[0]);
  const csvContent = [
    headers.join(','),
    ...SAMPLE_CSV_DATA.map(row => 
      headers.map(header => `"${row[header] || ''}"`).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'sample_multi_domain_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Domain management component
const DomainManager = ({ currentUserDomain, allowedDomains, onDomainsChange }) => {
  const [newDomain, setNewDomain] = useState('');
  const [showAddDomain, setShowAddDomain] = useState(false);

  const handleAddDomain = () => {
    if (newDomain.trim() && !allowedDomains.includes(newDomain.trim().toLowerCase())) {
      const updatedDomains = [...allowedDomains, newDomain.trim().toLowerCase()];
      onDomainsChange(updatedDomains);
      setNewDomain('');
      setShowAddDomain(false);
    }
  };

  const handleRemoveDomain = (domain) => {
    if (domain !== currentUserDomain) {
      const updatedDomains = allowedDomains.filter(d => d !== domain);
      onDomainsChange(updatedDomains);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.Building size={20} />
          Domain Access Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage which domains you can include in ID card generation. Your primary domain ({currentUserDomain}) is always included.
        </Typography>
        
        <List dense>
          {allowedDomains.map((domain) => (
            <ListItem key={domain} divider>
              <ListItemText 
                primary={domain}
                secondary={domain === currentUserDomain ? 'Your primary domain' : 'Additional domain'}
              />
              <ListItemSecondaryAction>
                {domain === currentUserDomain ? (
                  <Chip size="small" label="Primary" color="primary" />
                ) : (
                  <Tooltip title="Remove domain">
                    <IconButton 
                      edge="end" 
                      size="small" 
                      onClick={() => handleRemoveDomain(domain)}
                      color="error"
                    >
                      <Icons.X size={16} />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {showAddDomain ? (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Domain (e.g., subsidiary.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              fullWidth
            />
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleAddDomain}
              disabled={!newDomain.trim()}
            >
              Add
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => {
                setShowAddDomain(false);
                setNewDomain('');
              }}
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icons.Plus />}
            onClick={() => setShowAddDomain(true)}
            sx={{ mt: 2 }}
          >
            Add Additional Domain
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`multi-upload-tabpanel-${index}`}
    aria-labelledby={`multi-upload-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const ImageBatchConverter = ({ onImagesConverted, convertedImages }) => {
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setConverting(true);
    setProgress(0);
    
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const base64 = await convertImageToBase64(file);
        results.push({
          id: `img_${Date.now()}_${i}`,
          name: file.name,
          originalName: file.name.replace(/\.[^/.]+$/, ""),
          size: file.size,
          base64,
          type: file.type
        });
      } catch (e) {
        console.error(`Error converting ${file.name}:`, e);
      }
      setProgress(((i + 1) / files.length) * 100);
    }
    
    onImagesConverted(results);
    setConverting(false);
    setProgress(0);
  };

  const handleRemoveImage = (imageId) => {
    const updated = convertedImages.filter(img => img.id !== imageId);
    onImagesConverted(updated);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<Icons.Upload />}
          disabled={converting}
        >
          {converting ? 'Converting...' : 'Select Images'}
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFiles}
          />
        </Button>
        
        {converting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Converting images... {Math.round(progress)}%
            </Typography>
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upload all photos at once. The system will try to auto-match based on filenames.
        </Typography>
      </Box>

      {convertedImages.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Converted Images ({convertedImages.length})
              </Typography>
              <Button
                size="small"
                color="secondary"
                onClick={() => onImagesConverted([])}
                startIcon={<Icons.Trash2 />}
              >
                Clear All
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              {convertedImages.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image.id}>
                  <Card variant="outlined" sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={image.base64}
                        alt={image.name}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'error.light', color: 'white' }
                        }}
                        onClick={() => handleRemoveImage(image.id)}
                      >
                        <Icons.X size={16} />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap title={image.name}>
                        {image.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {(image.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const PhotoSelector = ({ user, convertedImages, selectedPhoto, onPhotoSelect }) => {
  const getSuggestedPhotos = () => {
    if (!convertedImages.length) return [];
    
    const userName = user.name.toLowerCase().replace(/\s+/g, '');
    const userEmployeeId = user.employeeId.toLowerCase();
    
    return convertedImages.filter(img => {
      const imgName = img.originalName.toLowerCase().replace(/\s+/g, '');
      return imgName.includes(userName) || 
             imgName.includes(userEmployeeId) ||
             userName.includes(imgName);
    });
  };

  const suggestedPhotos = getSuggestedPhotos();

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel>Select Photo</InputLabel>
        <Select
          value={selectedPhoto || ''}
          onChange={(e) => onPhotoSelect(user.id, e.target.value)}
          label="Select Photo"
        >
          <MenuItem value="">
            <em>No photo</em>
          </MenuItem>
          {convertedImages.map((image) => (
            <MenuItem key={image.id} value={image.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={image.base64}
                  alt={image.name}
                  style={{
                    width: 24,
                    height: 24,
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                />
                <span>{image.name}</span>
                {suggestedPhotos.some(s => s.id === image.id) && (
                  <Chip size="small" label="Suggested" color="primary" variant="outlined" />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {suggestedPhotos.length > 0 && !selectedPhoto && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="primary">
            💡 Found {suggestedPhotos.length} suggested photo(s) based on name matching
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Enhanced CSV parsing with domain validation
const parseCSVData = (csvText, allowedDomains) => {
  return new Promise((resolve, reject) => {
    console.log('Starting CSV parse with allowed domains:', allowedDomains);
    
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      delimiter: '',
      newline: '',
      encoding: 'UTF-8',
      transformHeader: (header) => {
        return header.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      },
      transform: (value) => {
        return typeof value === 'string' ? value.trim() : value;
      },
      complete: (results) => {
        console.log('Papa Parse Results:', results);
        
        try {
          const processedData = results.data
            .filter(row => {
              const hasData = Object.values(row).some(value => 
                value && value.toString().trim() !== ''
              );
              return hasData;
            })
            .map((row, index) => {
              const getFieldValue = (fieldNames) => {
                for (const fieldName of fieldNames) {
                  const cleanFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
                  if (row[cleanFieldName]) {
                    return row[cleanFieldName].toString().trim();
                  }
                }
                return '';
              };
              
              const email = getFieldValue(['email', 'emailaddress', 'mail']) || '';
              const emailDomain = email.split('@')[1]?.toLowerCase() || '';
              
              return {
                id: index + 1,
                name: getFieldValue(['name', 'fullname', 'username', 'employeename']) || `User ${index + 1}`,
                email: email,
                emailDomain: emailDomain,
                phone: getFieldValue(['phone', 'phonenumber', 'mobile', 'contact']) || '+1 (555) 123-4567',
                company: getFieldValue(['company', 'companyname', 'organization', 'org']) || 'Company Name',
                department: getFieldValue(['department', 'dept', 'division']) || 'General',
                designation: getFieldValue(['designation', 'title', 'position', 'role', 'jobtitle']) || 'Employee',
                employeeId: getFieldValue(['employeeid', 'empid', 'id', 'staffid']) || `EMP-${String(index + 1).padStart(4, '0')}`,
                bloodGroup: getFieldValue(['bloodgroup', 'blood', 'bloodtype']) || 'Not Specified',
                location: getFieldValue(['location', 'address', 'city', 'office']) || 'Office Location',
                isAllowedDomain: allowedDomains.includes(emailDomain)
              };
            });
          
          console.log('Final processed data with domain validation:', processedData);
          resolve(processedData);
          
        } catch (error) {
          console.error('Error processing CSV data:', error);
          reject(error);
        }
      },
      error: (error) => {
        console.error('Papa Parse error:', error);
        reject(error);
      }
    });
  });
};

const MultiDomainCSVUploader = ({ open, onClose, onDataReady }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [convertedImages, setConvertedImages] = useState([]);
  const [photoMappings, setPhotoMappings] = useState({});
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoMatchAttempted, setAutoMatchAttempted] = useState(false);
  const [rawCsvPreview, setRawCsvPreview] = useState('');
  
  // Domain management state
  const [currentUserDomain, setCurrentUserDomain] = useState('');
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [includeAllDomains, setIncludeAllDomains] = useState(false);

  // Initialize current user domain
  useEffect(() => {
    const getCurrentUserDomain = () => {
      try {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const user = JSON.parse(userData);
          const domain = user.email?.split('@')[1]?.toLowerCase() || '';
          setCurrentUserDomain(domain);
          setAllowedDomains([domain]);
        }
      } catch (error) {
        console.error('Error getting current user domain:', error);
      }
    };

    if (open) {
      getCurrentUserDomain();
    }
  }, [open]);

  // Auto-match photos when both CSV and images are available
  useEffect(() => {
    if (csvData.length > 0 && convertedImages.length > 0 && !autoMatchAttempted) {
      autoMatchPhotos();
      setAutoMatchAttempted(true);
    }
  }, [csvData, convertedImages, autoMatchAttempted]);

  const autoMatchPhotos = () => {
    const newMappings = {};
    let matchCount = 0;

    csvData.forEach(user => {
      const userName = user.name.toLowerCase().replace(/\s+/g, '');
      const userEmployeeId = user.employeeId.toLowerCase();
      
      const matchedImage = convertedImages.find(img => {
        const imgName = img.originalName.toLowerCase().replace(/\s+/g, '');
        return imgName.includes(userName) || 
               imgName.includes(userEmployeeId) ||
               userName.includes(imgName);
      });

      if (matchedImage) {
        newMappings[user.id] = matchedImage.id;
        matchCount++;
      }
    });

    setPhotoMappings(newMappings);
    
    if (matchCount > 0) {
      setErrors(prev => [...prev, `✅ Auto-matched ${matchCount} photos based on filename similarity`]);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setLoading(true);
    setErrors([]);
    setRawCsvPreview('');
    
    const file = acceptedFiles[0];
    console.log('File selected:', file.name, 'Size:', file.size);
    
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file, 'UTF-8');
      });
      
      setRawCsvPreview(text.substring(0, 500));
      
      // Parse CSV with domain validation
      const processedData = await parseCSVData(text, includeAllDomains ? ['*'] : allowedDomains);
      
      if (processedData.length === 0) {
        throw new Error('No valid data found in CSV file. Please check the format.');
      }
      
      // Filter data based on domain permissions if not including all domains
      const filteredData = includeAllDomains ? 
        processedData : 
        processedData.filter(user => user.isAllowedDomain);
      
      const blockedUsers = processedData.length - filteredData.length;
      
      setCsvData(processedData); // Keep all data for display
      setActiveTab(1);
      setAutoMatchAttempted(false);
      
      const successMessages = [`✅ Successfully loaded ${processedData.length} records from CSV`];
      if (blockedUsers > 0 && !includeAllDomains) {
        successMessages.push(`⚠️ ${blockedUsers} users from non-allowed domains will be excluded from generation`);
      }
      setErrors(successMessages);
      
    } catch (error) {
      console.error('CSV processing error:', error);
      setErrors([`Error processing CSV: ${error.message}`]);
    } finally {
      setLoading(false);
    }
  }, [allowedDomains, includeAllDomains]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'text/plain': ['.csv']
    },
    multiple: false
  });

  const handlePhotoSelect = (userId, imageId) => {
    setPhotoMappings(prev => ({
      ...prev,
      [userId]: imageId
    }));
  };

  const handleConfirmUpload = () => {
    // Filter users based on domain permissions and merge with photos
    const allowedUsers = includeAllDomains ? 
      csvData : 
      csvData.filter(user => user.isAllowedDomain);
    
    const finalData = allowedUsers.map(user => {
      const selectedImageId = photoMappings[user.id];
      const selectedImage = convertedImages.find(img => img.id === selectedImageId);
      
      return {
        ...user,
        avatar: selectedImage ? selectedImage.base64 : null
      };
    });

    console.log('Multi-domain final data being sent:', finalData);
    onDataReady(finalData);
    onClose();
  };

  const getPhotoStats = () => {
    const allowedUsers = includeAllDomains ? csvData : csvData.filter(user => user.isAllowedDomain);
    const totalUsers = allowedUsers.length;
    const usersWithPhotos = Object.keys(photoMappings).filter(userId => 
      allowedUsers.some(user => user.id === parseInt(userId))
    ).length;
    return { totalUsers, usersWithPhotos };
  };

  const stats = getPhotoStats();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Multi-Domain CSV Upload with Photo Mapping</Typography>
          {stats.totalUsers > 0 && (
            <Chip
              label={`${stats.usersWithPhotos}/${stats.totalUsers} photos mapped`}
              color={stats.usersWithPhotos === stats.totalUsers ? "success" : "warning"}
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="1. Domain & CSV" icon={<Icons.FileText />} />
          <Tab 
            label="2. Convert Photos" 
            icon={
              <Badge badgeContent={convertedImages.length} color="primary">
                <Icons.Camera />
              </Badge>
            } 
          />
          <Tab 
            label="3. Map Photos" 
            icon={<Icons.UserCheck />}
            disabled={csvData.length === 0}
          />
        </Tabs>

        {errors.length > 0 && (
          <Alert 
            severity={errors.some(e => e.startsWith('✅')) ? "success" : errors.some(e => e.startsWith('⚠️')) ? "warning" : "error"} 
            sx={{ mt: 2 }}
            onClose={() => setErrors([])}
          >
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          {/* Domain Management Section */}
          <DomainManager
            currentUserDomain={currentUserDomain}
            allowedDomains={allowedDomains}
            onDomainsChange={setAllowedDomains}
          />

          {/* Include All Domains Toggle */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAllDomains}
                    onChange={(e) => setIncludeAllDomains(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">
                      Include All Domains (Override restrictions)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, CSV can include users from any domain regardless of the allowed domains list above.
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          {/* Sample CSV Download Section */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              📄 Multi-Domain CSV Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download the sample CSV template with examples from multiple domains.
            </Typography>
            <Button
              variant="outlined"
              onClick={downloadSampleCSV}
              startIcon={<Icons.Download />}
              sx={{ mr: 2 }}
            >
              Download Sample CSV
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Sample includes users from: company.com, subsidiary.com, partner.org
            </Typography>
          </Box>

          {/* CSV Upload Area */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 4,
              bgcolor: isDragActive ? 'primary.50' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            <input {...getInputProps()} />
            <Icons.Upload size={48} color="#1976d2" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {isDragActive ? 'Drop your CSV file here' : 'Upload Multi-Domain CSV File'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop your CSV file here, or click to select
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Processing CSV file...
              </Typography>
            </Box>
          )}

          {/* CSV Success with Domain Analysis */}
          {csvData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ✅ CSV Loaded Successfully ({csvData.length} records)
              </Typography>
              
              {/* Domain breakdown */}
              {!includeAllDomains && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Domain Breakdown:
                  </Typography>
                  {[...new Set(csvData.map(user => user.emailDomain))].map(domain => {
                    const userCount = csvData.filter(user => user.emailDomain === domain).length;
                    const isAllowed = allowedDomains.includes(domain);
                    return (
                      <Chip
                        key={domain}
                        label={`${domain}: ${userCount} users`}
                        color={isAllowed ? "success" : "error"}
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    );
                  })}
                </Box>
              )}
              
              <Button
                variant="contained"
                onClick={() => setActiveTab(1)}
                endIcon={<Icons.ArrowRight />}
              >
                Next: Convert Photos
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ImageBatchConverter
            onImagesConverted={setConvertedImages}
            convertedImages={convertedImages}
          />
          
          {convertedImages.length > 0 && csvData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => setActiveTab(2)}
                endIcon={<Icons.ArrowRight />}
              >
                Next: Map Photos to Users
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Map Photos to Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select photos for users. Only users from allowed domains {includeAllDomains ? '(all domains included)' : `(${allowedDomains.join(', ')})`} will be processed.
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Photo Selection</TableCell>
                  <TableCell>Preview</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvData.map((user) => {
                  const isAllowed = includeAllDomains || user.isAllowedDomain;
                  const selectedImageId = photoMappings[user.id];
                  const selectedImage = convertedImages.find(img => img.id === selectedImageId);
                  
                  return (
                    <TableRow key={user.id} sx={{ opacity: isAllowed ? 1 : 0.5 }}>
                      <TableCell>
                        {isAllowed ? (
                          <Chip size="small" label="✓" color="success" />
                        ) : (
                          <Tooltip title={`Domain ${user.emailDomain} not in allowed list`}>
                            <Chip size="small" label="✗" color="error" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.employeeId}</TableCell>
                      <TableCell>{user.department}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {isAllowed ? (
                          <PhotoSelector
                            user={user}
                            convertedImages={convertedImages}
                            selectedPhoto={selectedImageId}
                            onPhotoSelect={handlePhotoSelect}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Domain not allowed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAllowed && selectedImage ? (
                          <Avatar
                            src={selectedImage.base64}
                            alt={user.name}
                            sx={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                            <Icons.User size={20} />
                          </Avatar>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        
        {activeTab === 2 && csvData.length > 0 && (
          <Button
            variant="contained"
            onClick={handleConfirmUpload}
            disabled={loading || stats.totalUsers === 0}
            startIcon={<Icons.Check />}
          >
            Confirm & Generate {stats.totalUsers} ID Cards
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MultiDomainCSVUploader;